import moment from "moment";
import {
	AssessmentOrdersOverviewPerspective,
	Company,
	ThemeColor,
	TransportOrderPerspective,
	TransportOrderStatus,
	UserReport,
	UserReportAssessmentOrdersExportParameters,
	UserReportBcaSoldSalesExportParameters,
	UserReportCarmarketPreparationParameters,
	UserReportTransportOrdersExportParameters,
	UserReportValidationSlaExportParameters,
	UserReportVehiclesExportParameters,
	VehiclePerspective,
} from "../../system/Domain";
import dateFormats from "../../system/dateFormats";
import { translateVehicleRegistrationDocumentStatus } from "../Vehicles/DocumentManagement/VehicleRegistrationDocumentsStatusPill";

// Shared data model for displaying the parameters a UserReport was generated with. Two
// presentations are built on top of it (see UserReportListItem.tsx): condensed chips
// (UserReportParameterChips.tsx) and a full "Name: value" list (UserReportParameterList.tsx). Both
// reuse the exact same coloring/importance rules below.
//
// This lives entirely in the frontend rather than being modeled on the backend
// UserReport*Parameters DTOs, because (a) importance/color are pure display concerns that
// shouldn't leak into the domain model, and (b) all other enum/label formatting for this app
// already lives here (see e.g. VehiclesSidebar.tsx, TransportOrdersSidebar.tsx,
// translateVehicleRegistrationDocumentStatus).
export interface UserReportParameter {
	key: string;
	name: string;
	value: string;
	color: ThemeColor;
	// Emphasized (bold) - always true for Perspective, all dates, AssignedTo and Company.
	important: boolean;
}

export interface UserReportParameterContext {
	companies: Company[];
}

const isEmpty = (value: unknown) =>
	value === undefined ||
	value === null ||
	value === "" ||
	(Array.isArray(value) && value.length === 0);

const formatDate = (value?: string | null) => (isEmpty(value) ? undefined : moment(value!).format(dateFormats.date));

// `color="info"` is what ListItemChip actually renders in grey (see ListItemChip.tsx) - `"grey"`
// falls through to the primary styling there, so it's never used here despite being a valid
// ThemeColor value elsewhere in the app.
const GREY: ThemeColor = "info";
const PRIMARY: ThemeColor = "primary";

const makeParameter = (key: string, name: string, value: string, color: ThemeColor = PRIMARY, important = false): UserReportParameter => ({
	key,
	name,
	value,
	color,
	important,
});

// Dates are always primary and always important, per the Reports page parameter display design.
const dateParameter = (key: string, name: string, value?: string | null): UserReportParameter | undefined => {
	const formatted = formatDate(value);
	return formatted ? makeParameter(key, name, formatted, PRIMARY, true) : undefined;
};

const vehiclePerspectiveLabels: Record<VehiclePerspective, string> = {
	Stock: "Bestand",
	In: "Eingang",
	Out: "Ausgang",
	All: "Alle",
	InventoryOpen: "Inventur offen",
	InventoryDone: "Inventur erledigt",
	Bookmarked: "Merkliste",
};

const transportOrderPerspectiveLabels: Record<TransportOrderPerspective, string> = {
	All: "Alle",
	Active: "Aktiv",
	Cancelled: "Zurückgegeben",
	Unconfirmed: "Unbestätigt",
	Confirmed: "Bestätigt",
	ScheduledForReturn: "Geplante Anlieferungen",
	OverdueForReturn: "Überfällige Rückgaben",
	Completed: "Abgeschlossen",
	Outbound: "Auslieferungen",
};

const transportOrderStatusLabels: Record<TransportOrderStatus, string> = {
	All: "Alle",
	Created: "Erzeugt",
	Assigned: "Zugewiesen",
	Completed: "Abgeschlossen",
	Cancelled: "Zurückgegeben",
};

const transportOrderTypeLabels: Record<string, string> = {
	CustomerDelivery: "Eigenanlieferung",
	Delivery: "Auslieferung",
	Arrival: "Anlieferung",
	ServiceArrival: "Logistiker Anlieferung",
	Internal: "Intern",
	CrossTransport: "Quertransport",
};

// AssessmentOrdersOverviewPerspective (frontend) is missing "Changed"/"Complained", which the
// backend AssessmentOrderPerspective enum has (see AssessmentOrderPerspective.cs) - falls back to
// the raw value below for those, same as any other not-yet-translated enum value.
const assessmentOrderPerspectiveLabels: Record<AssessmentOrdersOverviewPerspective, string> = {
	All: "Alle",
	Processed: "Beauftragt",
	Unprocessed: "Neu",
	Cancelled: "Storniert",
	Completed: "Abgeschlossen",
	Interrupted: "Unterbrochen",
	Own: "Von mir erstellt",
};

const buildCarmarketPreparationParameters = (params: UserReportCarmarketPreparationParameters): UserReportParameter[] =>
	compact([dateParameter("date", "Datum", params.date)]);

const buildBcaSoldSalesExportParameters = (params: UserReportBcaSoldSalesExportParameters): UserReportParameter[] =>
	compact([dateParameter("date", "Datum", params.date)]);

const buildValidationSlaExportParameters = (params: UserReportValidationSlaExportParameters): UserReportParameter[] =>
	compact([dateParameter("dateFrom", "Ab", params.dateFrom)]);

const buildVehiclesExportParameters = (
	params: UserReportVehiclesExportParameters,
	{ companies }: UserReportParameterContext,
): UserReportParameter[] => {
	const company = isEmpty(params.companyId) ? undefined : companies.find((c) => c.id === params.companyId);

	return compact([
		makeParameter("perspective", "Perspektive", vehiclePerspectiveLabels[params.perspective] ?? params.perspective, PRIMARY, true),
		dateParameter("date", "Datum", params.date),
		dateParameter("currentEntryDateFrom", "Eingang ab", params.currentEntryDateFrom),
		dateParameter("currentExitDateFrom", "Ausgang ab", params.currentExitDateFrom),
		dateParameter("registrationDocumentStatusDateFrom", "Statuswechsel ab", params.registrationDocumentStatusDateFrom),
		isEmpty(params.status) ? undefined : makeParameter("status", "Status", params.status!),
		company
			? makeParameter("companyId", "Firma", company.name, PRIMARY, true)
			: isEmpty(params.companyId)
				? undefined
				: makeParameter("companyId", "Firma", `${params.companyId}`, PRIMARY, true),
		isEmpty(params.locationName) ? undefined : makeParameter("locationName", "Standort", params.locationName!),
		isEmpty(params.placeGroup) ? undefined : makeParameter("placeGroup", "Platzgruppe", params.placeGroup!),
		isEmpty(params.placeId) ? undefined : makeParameter("placeId", "Platz", params.placeId!),
		isEmpty(params.inventoryId) ? undefined : makeParameter("inventoryId", "Inventur", params.inventoryId!),
		isEmpty(params.vin) ? undefined : makeParameter("vin", "FIN", params.vin!),
		isEmpty(params.plateNumber) ? undefined : makeParameter("plateNumber", "Kennzeichen", params.plateNumber!),
		isEmpty(params.fullText) ? undefined : makeParameter("fullText", "Suche", params.fullText!),
		isEmpty(params.registrationDocumentImportId)
			? undefined
			: makeParameter("registrationDocumentImportId", "Zulassungsdokument-Import", params.registrationDocumentImportId!),
		isEmpty(params.registrationDocumentStatus)
			? undefined
			: makeParameter(
					"registrationDocumentStatus",
					"Zulassungsdokument-Status",
					translateVehicleRegistrationDocumentStatus(params.registrationDocumentStatus),
				),
	]);
};

const buildTransportOrdersExportParameters = (params: UserReportTransportOrdersExportParameters): UserReportParameter[] => {
	const status = (params.status ?? []).filter((s) => s !== "All");

	return compact([
		makeParameter("perspective", "Perspektive", transportOrderPerspectiveLabels[params.perspective] ?? params.perspective, PRIMARY, true),
		dateParameter("dateFrom", "Ab", params.dateFrom),
		dateParameter("dateCreated", "Erstellt", params.dateCreated),
		dateParameter("plannedReturnDate", "Geplante Rückgabe", params.plannedReturnDate),
		isEmpty(params.assignedTo) ? undefined : makeParameter("assignedTo", "Dienstleister", params.assignedTo!, PRIMARY, true),
		isEmpty(status) ? undefined : makeParameter("status", "Status", status.map((s) => transportOrderStatusLabels[s] ?? s).join(", ")),
		isEmpty(params.pickupCompound) ? undefined : makeParameter("pickupCompound", "Abholungsstandort", params.pickupCompound!),
		isEmpty(params.destinationCompound) ? undefined : makeParameter("destinationCompound", "Rückgabestandort", params.destinationCompound!),
		isEmpty(params.orderType) ? undefined : makeParameter("orderType", "Auftragsart", transportOrderTypeLabels[params.orderType!] ?? params.orderType!),
		isEmpty(params.vin) ? undefined : makeParameter("vin", "FIN", params.vin!),
		isEmpty(params.plateNumber) ? undefined : makeParameter("plateNumber", "Kennzeichen", params.plateNumber!),
		isEmpty(params.orderPlateNumber) ? undefined : makeParameter("orderPlateNumber", "Auftrags-Kennzeichen", params.orderPlateNumber!),
		isEmpty(params.vehicleId) ? undefined : makeParameter("vehicleId", "Fahrzeug", params.vehicleId!),
		isEmpty(params.importId) ? undefined : makeParameter("importId", "Import", params.importId!),
		isEmpty(params.orderId) ? undefined : makeParameter("orderId", "Auftrag", params.orderId!),
		isEmpty(params.returnOrderId) ? undefined : makeParameter("returnOrderId", "Rückgabeauftrag", params.returnOrderId!),
		isEmpty(params.fullText) ? undefined : makeParameter("fullText", "Suche", params.fullText!),
		params.confirmed === true ? makeParameter("confirmed", "Bestätigung", "Bestätigt") : undefined,
		params.confirmed === false ? makeParameter("confirmed", "Bestätigung", "Unbestätigt", GREY) : undefined,
		params.hasReturnOrder === true ? makeParameter("hasReturnOrder", "Rückgabeauftrag vorhanden", "Ja") : undefined,
		params.hasReturnOrder === false ? makeParameter("hasReturnOrder", "Rückgabeauftrag vorhanden", "Nein", GREY) : undefined,
		params.scheduledForReturn ? makeParameter("scheduledForReturn", "Geplante Rückgabe", "Ja") : undefined,
		params.overdueReturns ? makeParameter("overdueReturns", "Überfällige Rückgabe", "Ja") : undefined,
	]);
};

const buildAssessmentOrdersExportParameters = (params: UserReportAssessmentOrdersExportParameters): UserReportParameter[] =>
	compact([
		makeParameter("perspective", "Perspektive", assessmentOrderPerspectiveLabels[params.perspective] ?? params.perspective, PRIMARY, true),
		dateParameter("dateFrom", "Ab", params.dateFrom),
		dateParameter("dateTo", "Bis", params.dateTo),
		isEmpty(params.assessor) ? undefined : makeParameter("assessor", "Gutachter", params.assessor!, PRIMARY, true),
		isEmpty(params.id) ? undefined : makeParameter("id", "Auftrag", params.id!),
		isEmpty(params.contractNumber) ? undefined : makeParameter("contractNumber", "Vertrag", `${params.contractNumber}`),
		isEmpty(params.vin) ? undefined : makeParameter("vin", "FIN", params.vin!),
		isEmpty(params.plateNumber) ? undefined : makeParameter("plateNumber", "Kennzeichen", params.plateNumber!),
		isEmpty(params.vehicleId) ? undefined : makeParameter("vehicleId", "Fahrzeug", params.vehicleId!),
		isEmpty(params.fullText) ? undefined : makeParameter("fullText", "Suche", params.fullText!),
		params.isTransferred === true ? makeParameter("isTransferred", "Transferiert", "Ja") : undefined,
		params.isTransferred === false ? makeParameter("isTransferred", "Transferiert", "Nein", GREY) : undefined,
		params.hasInquiryRequest === true ? makeParameter("hasInquiryRequest", "Rückfrage vorhanden", "Ja") : undefined,
		params.hasInquiryRequest === false ? makeParameter("hasInquiryRequest", "Rückfrage vorhanden", "Nein", GREY) : undefined,
		params.hasDefect === true ? makeParameter("hasDefect", "Mangel vorhanden", "Ja") : undefined,
		params.hasDefect === false ? makeParameter("hasDefect", "Mangel vorhanden", "Nein", GREY) : undefined,
	]);

const compact = (parameters: (UserReportParameter | undefined)[]): UserReportParameter[] =>
	parameters.filter((p): p is UserReportParameter => !!p);

export const buildUserReportParameters = (userReport: UserReport, ctx: UserReportParameterContext): UserReportParameter[] => {
	if (userReport.carmarketPreparation) return buildCarmarketPreparationParameters(userReport.carmarketPreparation);
	if (userReport.validationSlaExport) return buildValidationSlaExportParameters(userReport.validationSlaExport);
	if (userReport.vehiclesExport) return buildVehiclesExportParameters(userReport.vehiclesExport, ctx);
	if (userReport.transportOrdersExport) return buildTransportOrdersExportParameters(userReport.transportOrdersExport);
	if (userReport.assessmentOrdersExport) return buildAssessmentOrdersExportParameters(userReport.assessmentOrdersExport);
	if (userReport.bcaSoldSalesExport) return buildBcaSoldSalesExportParameters(userReport.bcaSoldSalesExport);
	return [];
};

// Condensed chips - all "important" parameters are candidates (Perspective, Company/AssignedTo/
// Assessor, all dates), but we only ever show up to 3 chips, so the tile stays scannable. Non-date
// "identity" parameters (perspective, company/assignedTo/assessor) are shown first, dates fill the
// remaining slots. If there are no important parameters at all, no chips are shown (we
// deliberately don't fall back to less important ones - that's what the list view is for).
const MAX_CONDENSED_PARAMETERS = 3;

const isDateParameter = (parameter: UserReportParameter) => parameter.key.toLowerCase().includes("date");

export const buildCondensedUserReportParameters = (
	userReport: UserReport,
	ctx: UserReportParameterContext,
): UserReportParameter[] => {
	const important = buildUserReportParameters(userReport, ctx).filter((p) => p.important);
	const identity = important.filter((p) => !isDateParameter(p));
	const dates = important.filter(isDateParameter);

	return [...identity, ...dates].slice(0, MAX_CONDENSED_PARAMETERS);
};
